/**
 * Довідки-розрахунки переоцінки валюти — список (канон Норми #29):
 * .card + InfiniteDocGrid (серверна пагінація) + фільтри організація/дати.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { fxRevaluations, type FxRevaluationListItem } from '@/api/fxRevaluations';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const MONTHS_SHORT = ['', 'січень', 'лютий', 'березень', 'квітень', 'травень',
  'червень', 'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

// «липень 2026» або «травень 2026 — серпень 2026» (період довідки).
function fmtPeriod(r: FxRevaluationListItem): string {
  const m = (d: string) => `${MONTHS_SHORT[Number(d.slice(5, 7))]} ${d.slice(0, 4)}`;
  const start = r.period_start ?? r.revaluation_date;
  const end = r.period_end ?? r.revaluation_date;
  if (!start) return '';
  return start.slice(0, 7) === end.slice(0, 7) ? m(start) : `${m(start)} — ${m(end)}`;
}

export default function FxRevaluationsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const [fltFrom, setFltFrom] = useState(gFrom?.slice(0, 10) ?? '');
  const [fltTo, setFltTo] = useState(gTo?.slice(0, 10) ?? '');
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const { datasource, total } = useInfiniteGridDatasource<FxRevaluationListItem>(
    (limit, offset, sort) => fxRevaluations.list({
      organization_id: orgId || undefined,
      date_from: fltFrom || undefined,
      date_to: fltTo || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, fltFrom, fltTo],
    { attachmentsType: 'doc.fx_revaluations' },
  );

  const cols = useMemo<ColDef<FxRevaluationListItem>[]>(() => [
    { field: 'document_number', headerName: '№', width: 150 },
    {
      colId: 'organization_id', field: 'org_name', headerName: 'Організація',
      width: 220,
    },
    {
      field: 'document_date', headerName: 'Дата документа', width: 160,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      colId: 'period_start', headerName: 'Період переоцінки', flex: 1, minWidth: 200,
      valueGetter: (p) => p.data ? fmtPeriod(p.data) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>
          Переоцінка валюти (курсові різниці)<TitleTotal total={total} />
        </h2>
        <button className="primary" onClick={() => navigate('/fx-revaluations/new')}>
          сформувати довідку
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 240 }}>
          <option value="">Усі організації</option>
          {(orgsQ.data?.items ?? []).map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltFrom} onChange={setFltFrom} />
        </div>
        <span style={{ color: 'var(--muted)' }}>—</span>
        <div style={{ width: 120, flex: '0 0 auto' }}>
          <DatePopover value={fltTo} onChange={setFltTo} />
        </div>
      </div>
      <InfiniteDocGrid<FxRevaluationListItem>
        datasource={datasource}
        columnDefs={cols}
        storageKey="fx-revaluations"
        onRowDoubleClicked={(r) => navigate(`/fx-revaluations/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
